import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calculator, BookOpen, List, Wine, Beer, Coffee, GlassWater, Martini, Grape } from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cuantas copas salen de una botella de vino?',
    a: 'De una botella de vino estandar (75 cl) salen entre 5 y 6 copas, dependiendo del servicio. Con un servicio estandar de 12,5 cl por copa salen 6 copas. Con un servicio generoso de 15 cl salen 5 copas. Hay que añadir un 5-8% de merma por cata, restos de botella y posibles derrames, lo que reduce el rendimiento efectivo.',
  },
  {
    q: '¿Cuantos gin tonics salen de una botella?',
    a: 'De una botella de ginebra de 70 cl salen 14 gin tonics con un servicio estandar de 5 cl por copa. Con un servicio generoso de 6 cl salen unos 11-12. Si usas dosificador de 5 cl, el rendimiento es exacto. Sin dosificador, el over-pouring puede reducirlo a 10-11 gin tonics por botella.',
  },
  {
    q: '¿Que food cost es normal en un bar?',
    a: 'El food cost objetivo de bebidas en un bar esta entre el 18% y el 25%, significativamente menor que en comida (28-35%). Los refrescos y cafes tienen food cost del 10-18%, los cocteles del 15-25%, y el vino por copa del 25-33%. Un bar bien gestionado tiene un food cost global de bebidas del 20-22%.',
  },
  {
    q: '¿Como controlo el over-pouring?',
    a: 'El over-pouring (servir mas de la cuenta) es la principal causa de merma en cocteles. Para controlarlo: 1) Usa dosificadores en todas las botellas de licor. 2) Obliga al uso de jigger para cocteles. 3) Haz inventarios semanales de botellas. 4) Compara el consumo teorico (segun ventas) con el consumo real (segun inventario). La diferencia es el over-pouring.',
  },
  {
    q: '¿Debo incluir el hielo en el escandallo?',
    a: 'Si, el hielo es un ingrediente y debe incluirse en el escandallo. Aunque su coste unitario es bajo (0,03-0,08 euros por copa), en un bar que sirve 100 bebidas diarias con hielo, supone entre 90 y 240 euros al mes. El coste del hielo depende de si lo produces (maquina propia) o lo compras (bolsas).',
  },
  {
    q: '¿Como calculo el escandallo de un coctel de autor?',
    a: 'Igual que cualquier otro coctel, pero con mas ingredientes: 1) Lista cada componente con su cantidad exacta (usa jigger o balanza). 2) Calcula el coste unitario de cada ingrediente dividiendo el precio de compra entre las unidades que salen. 3) Suma todos los costes. 4) Añade hielo y garnish. Los cocteles de autor suelen tener food cost del 20-30% porque usan ingredientes premium.',
  },
  {
    q: '¿Cuantos cafes salen de un kilo de cafe?',
    a: 'De un kilo de cafe en grano salen entre 120 y 145 cafes, dependiendo de la dosis. La dosis estandar para espresso es de 7 g (142 cafes/kg). Para cafe doble, 14 g (71 cafes/kg). Para cafe de filtro, 10-12 g (83-100 cafes/kg). La merma del cafe es minima (1-2%) por calibracion de la molienda y derrames.',
  },
  {
    q: '¿El vino por copa es rentable?',
    a: 'Depende de la gestion. Con un food cost del 25-33%, el vino por copa puede ser rentable si: 1) Sacas 6 copas por botella (no 5). 2) Usas sistemas de conservacion (gas argon, Coravin) para evitar oxidacion. 3) Rotas las referencias para minimizar botellas abiertas. 4) Aplicas la regla del sector: PVP copa = precio coste botella / 3-4. El principal riesgo es la oxidacion de botellas abiertas que no se venden a tiempo.',
  },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Escandallo de Bebidas — Como Calcular el Coste de Cocteles, Vinos y Cafes',
  description: 'Calcula el food cost de bebidas con 6 ejemplos reales: gin tonic, mojito, cafe, vino por copa, cerveza de barril y zumo natural. Tabla de mermas y food cost objetivo por tipo de bebida.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' } },
  datePublished: '2026-06-17',
  dateModified: '2026-06-17',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    { '@type': 'ListItem', position: 3, name: 'Escandallo de Bebidas', item: 'https://pleko.app/escandallo-bebidas' },
  ],
};

const itemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: '6 Ejemplos de Escandallos de Bebidas',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Escandallo Gin Tonic Premium' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo Mojito Clasico' },
    { '@type': 'ListItem', position: 3, name: 'Escandallo Cafe con Leche' },
    { '@type': 'ListItem', position: 4, name: 'Escandallo Vino por Copa (Ribera del Duero Crianza)' },
    { '@type': 'ListItem', position: 5, name: 'Escandallo Cerveza de Barril (Caña 33cl)' },
    { '@type': 'ListItem', position: 6, name: 'Escandallo Zumo de Naranja Natural' },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que-diferente', label: '¿Por que el escandallo de bebidas es diferente?' },
  { id: 'food-cost-objetivo', label: 'Food cost objetivo por tipo de bebida' },
  { id: 'ejemplos', label: '6 ejemplos detallados de escandallos' },
  { id: 'reducir-food-cost', label: 'Como reducir el food cost de bebidas' },
  { id: 'mermas-bebidas', label: 'Mermas especificas de bebidas' },
  { id: 'bebidas-vs-comida', label: 'Bebidas vs comida: diferencias clave' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function EscandalloBebidasPage() {
  return (
    <>
      <Helmet>
        <title>Escandallo de Bebidas — Cocteles, Vinos y Cafes con Ejemplos Reales | PLEKO</title>
        <meta
          name="description"
          content="Calcula el food cost de bebidas con 6 ejemplos reales: gin tonic, mojito, cafe, vino por copa, cerveza de barril y zumo natural. Tabla de mermas y food cost objetivo por tipo de bebida."
        />
        <meta
          name="keywords"
          content="escandallo bebidas, escandallo de bebidas, food cost bebidas, escandallo coctel, escandallo bar, coste copa vino, food cost bar, escandallo gin tonic, coste coctel, escandallo cafeteria, rentabilidad bebidas restaurante, escandallo cerveza, escandallo mojito"
        />
        <link rel="canonical" href="https://pleko.app/escandallo-bebidas" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/escandallo-bebidas" />
        <meta property="og:title" content="Escandallo de Bebidas — Cocteles, Vinos y Cafes con Ejemplos Reales" />
        <meta property="og:description" content="Calcula el food cost de bebidas con 6 ejemplos reales. Tabla de mermas y food cost objetivo por tipo de bebida." />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-purple-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
                <Martini className="w-4 h-4" />
                Guia de costes de bebidas 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Escandallo de <span className="text-purple-500">Bebidas</span> — Como Calcular el Coste de Cocteles, Vinos y Cafes
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Las bebidas son el producto mas rentable de tu negocio. Aprende a calcular escandallos de cocteles, vinos por copa, cervezas, cafes y zumos con ejemplos reales y precios actualizados.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#ejemplos"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-colors shadow-lg shadow-purple-200"
                >
                  Ver ejemplos de escandallos
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calculadora de costes
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-purple-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-purple-600 hover:text-purple-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">

            {/* Section 1: Por que es diferente */}
            <h2 id="por-que-diferente" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">¿Por que el escandallo de bebidas es diferente?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>escandallo de bebidas</strong> comparte la logica del <Link to="/escandallo" className="text-purple-600 hover:underline">escandallo clasico de cocina</Link>, pero tiene particularidades que lo convierten en una herramienta distinta. Mientras que un plato puede tener 10-20 ingredientes con mermas complejas de coccion, una bebida tiene 3-5 componentes y su complejidad esta en otro sitio: en el <strong>control de las medidas y las mermas invisibles</strong>.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las bebidas son el producto con <strong>mejor margen de todo el negocio hostelero</strong>. Mientras la comida suele tener un food cost del 28-35%, las bebidas oscilan entre el 15% y el 25%. Un restaurante que factura un 30% en bebidas puede estar generando hasta el 40-50% de su beneficio bruto con esa partida. Por eso, controlar el escandallo de bebidas no es opcional: es donde esta el dinero.
            </p>

            <div className="bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
              <p className="text-purple-800 font-medium text-center text-lg">
                Food cost objetivo de bebidas vs comida
              </p>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600">18-22%</p>
                  <p className="text-sm text-slate-600 mt-1">Food cost objetivo bebidas</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-500">28-32%</p>
                  <p className="text-sm text-slate-600 mt-1">Food cost objetivo comida</p>
                </div>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las <strong>mermas en bebidas son diferentes</strong> a las de cocina. No hay limpieza ni coccion, pero hay derrames, sobre-servido (over-pouring), producto caducado, restos de botella y el coste oculto del hielo. El over-pouring es el gran enemigo invisible: un camarero que sirve 6 cl de ginebra en vez de 5 cl esta regalando un 20% del producto sin que nadie lo detecte.
            </p>

            <h3 className="text-xl font-bold text-slate-800 mb-4">5 claves del escandallo de bebidas</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Menos ingredientes, mas margen — pero el margen se pierde si no estandarizas medidas',
                'El hielo es un ingrediente oculto que debe incluirse en el coste (0,03-0,08 euros por bebida)',
                'El over-pouring puede representar entre un 10% y un 20% de merma invisible en licores',
                'Estandarizar medidas con jigger y dosificador es obligatorio para mantener el food cost',
                'Las mermas de vino abierto (oxidacion) y cerveza de barril (espuma, purga) son especificas del sector',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Section 2: Tabla de food cost objetivo */}
            <h2 id="food-cost-objetivo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Tabla de Food Cost Objetivo por Tipo de Bebida</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              No todas las bebidas tienen el mismo margen. Esta tabla recoge los <strong>rangos de food cost objetivo</strong> que manejan los bares y restaurantes rentables en Espana. Si tu food cost esta por encima de estos rangos, estas perdiendo dinero. Si esta por debajo, probablemente estas cobrando bien o has negociado buenos precios con tus distribuidores.
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-purple-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de bebida</th>
                    <th className="p-3 text-center font-medium">Food Cost Objetivo</th>
                    <th className="p-3 text-center font-medium">Margen habitual</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cocteles clasicos', '15-20%', 'Alto', 'bg-green-50'],
                    ['Cocteles premium', '20-25%', 'Medio-alto', 'bg-green-50'],
                    ['Cerveza de barril', '20-25%', 'Medio', 'bg-yellow-50'],
                    ['Cerveza botella', '25-30%', 'Medio', 'bg-yellow-50'],
                    ['Vino por copa', '25-33%', 'Medio', 'bg-yellow-50'],
                    ['Vino por botella', '30-40%', 'Medio-bajo', 'bg-orange-50'],
                    ['Refrescos', '10-15%', 'Muy alto', 'bg-green-50'],
                    ['Cafe', '12-18%', 'Muy alto', 'bg-green-50'],
                    ['Zumos naturales', '25-35%', 'Medio', 'bg-yellow-50'],
                    ['Agua mineral', '5-10%', 'Muy alto', 'bg-green-50'],
                  ].map(([tipo, foodCost, margen, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-purple-700">{foodCost}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{margen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Observa como los <strong>refrescos, cafes y agua mineral</strong> tienen food costs del 5-18%, lo que los convierte en los productos mas rentables por unidad. Por eso, muchos bares potencian la venta de cafe de especialidad o refrescos artesanales: el margen es excepcional. En cambio, el <strong>vino por botella</strong> tiene el peor ratio porque el cliente conoce el precio de compra en supermercado y no acepta multiplicadores excesivos. <Link to="/food-cost" className="text-purple-600 hover:underline">Mas sobre food cost en hosteleria</Link>.
            </p>

            {/* Section 3: 6 Ejemplos */}
            <h2 id="ejemplos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">6 Ejemplos Detallados de Escandallos de Bebidas</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              A continuacion, 6 escandallos reales con <strong>precios de distribuidor en Espana (junio 2026)</strong>. Cada ejemplo incluye el desglose completo, el food cost resultante y consejos profesionales para optimizarlo.
            </p>

            {/* Ejemplo 1: Gin Tonic Premium */}
            <h3 id="ejemplo-gin-tonic" className="text-2xl font-bold text-slate-900 mb-4 scroll-mt-20">
              <span className="flex items-center gap-2"><Martini className="w-6 h-6 text-purple-500" /> Ejemplo 1: Gin Tonic Premium</span>
            </h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El gin tonic es el <strong>coctel mas vendido en bares de Espana</strong>. Su escandallo depende enormemente de la marca de ginebra y tonica que uses. Aqui lo calculamos con producto premium:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-purple-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Gin Hendrick's", '5 cl', 'Botella 70cl = 14 gin tonics. 35,00 euros/bot', '2,50 euros'],
                    ['Tonica Fever-Tree', '20 cl', 'Botella individual', '1,80 euros'],
                    ['Pepino (2 rodajas)', '10 g', 'Garnish', '0,05 euros'],
                    ['Pimienta rosa (3-4 granos)', '1 g', 'Garnish', '0,03 euros'],
                    ['Hielo', '200 g', 'Maquina propia', '0,08 euros'],
                  ].map(([ing, qty, calc, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calc}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-purple-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-purple-600 text-lg">4,46 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-purple-600">4,46 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">12,00 euros</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-red-600">37,2%</p>
                <p className="text-xs text-red-500 font-medium">ALERTA — Revisar</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                Un gin tonic con Hendrick's a 12 euros tiene un food cost del 37,2%, por encima del objetivo. <strong>Solucion:</strong> sube el PVP a 14 euros (food cost 31,9%) o usa una ginebra de calidad similar a 25 euros/botella, lo que baja el coste a 3,74 euros y el food cost al 31,2%. Otra opcion: usar tonica de 1,20 euros en vez de Fever-Tree, bajando el coste total a 3,86 euros (food cost 32,2%).
              </p>
            </div>

            {/* Ejemplo 2: Mojito Clasico */}
            <h3 id="ejemplo-mojito" className="text-2xl font-bold text-slate-900 mb-4 scroll-mt-20">
              <span className="flex items-center gap-2"><GlassWater className="w-6 h-6 text-green-500" /> Ejemplo 2: Mojito Clasico</span>
            </h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El mojito es uno de los <strong>cocteles mas rentables</strong> de la cocteleria clasica. Sus ingredientes son economicos y el resultado justifica un PVP alto. Aqui tienes el desglose:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-green-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Ron Havana Club 3 anos', '5 cl', 'Botella 70cl a 15,00 euros = 14 mojitos', '1,07 euros'],
                    ['Lima (media lima)', '40 g', 'Limas a 3,80 euros/kg', '0,15 euros'],
                    ['Hierbabuena', '5 g', '6-8 hojas. Manojo 20 euros/kg', '0,10 euros'],
                    ['Azucar', '15 g', 'Azucar blanco a 1,20 euros/kg', '0,02 euros'],
                    ['Soda / agua con gas', '10 cl', 'Botella 1L a 0,80 euros', '0,08 euros'],
                    ['Hielo picado', '200 g', 'Maquina propia', '0,08 euros'],
                  ].map(([ing, qty, calc, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calc}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-green-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-green-600 text-lg">1,50 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-green-600">1,50 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">9,00 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">16,7%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Con un food cost del 16,7%, el mojito es un <strong>coctel estrella</strong> para la rentabilidad de cualquier bar. Cada mojito genera <strong>7,50 euros de margen bruto</strong>. Si vendes 20 mojitos al dia en temporada alta, son 150 euros de margen solo con este coctel. El secreto esta en que la lima, la hierbabuena y el azucar cuestan centimos.
            </p>

            {/* Ejemplo 3: Cafe con Leche */}
            <h3 id="ejemplo-cafe" className="text-2xl font-bold text-slate-900 mb-4 scroll-mt-20">
              <span className="flex items-center gap-2"><Coffee className="w-6 h-6 text-amber-700" /> Ejemplo 3: Cafe con Leche</span>
            </h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El cafe es el producto con <strong>mayor volumen de venta</strong> en la mayoria de bares y cafeterias espanolas. Aunque el margen unitario parece pequeno, el volumen lo convierte en un pilar de rentabilidad:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cafe en grano', '7 g', '1 kg = 142 cafes. 14,00 euros/kg', '0,10 euros'],
                    ['Leche entera', '200 ml', '0,90 euros/litro', '0,18 euros'],
                    ['Sobre de azucar', '1 ud', '0,02 euros/sobre', '0,02 euros'],
                  ].map(([ing, qty, calc, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calc}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-amber-700 text-lg">0,30 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-amber-700">0,30 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">1,80 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">16,7%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Con un food cost del 16,7%, el cafe con leche es <strong>uno de los productos mas rentables</strong> de cualquier establecimiento. Un bar que sirve 150 cafes al dia a 1,80 euros genera 270 euros de facturacion con solo 45 euros de coste en materia prima. Si usas cafe de especialidad a 22 euros/kg, el coste sube a 0,33 euros (food cost 18,3%), aun excelente, y justifica un PVP de 2,00-2,50 euros.
            </p>

            {/* Ejemplo 4: Vino por Copa */}
            <h3 id="ejemplo-vino" className="text-2xl font-bold text-slate-900 mb-4 scroll-mt-20">
              <span className="flex items-center gap-2"><Grape className="w-6 h-6 text-red-600" /> Ejemplo 4: Vino por Copa (Ribera del Duero Crianza)</span>
            </h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El vino por copa es una de las bebidas con <strong>mayor riesgo de perdida</strong> en hosteleria. La clave esta en cuantas copas sacas realmente de cada botella y cuanta merma tienes por oxidacion:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-red-800 text-white">
                    <th className="p-3 text-left font-medium">Concepto</th>
                    <th className="p-3 text-right font-medium">Detalle</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Botella Ribera del Duero Crianza', 'Coste restaurante', '18,50 euros'],
                    ['Copas por botella (servicio estandar 12,5 cl)', '75 cl / 12,5 cl = 6 copas', '3,08 euros/copa'],
                    ['Merma (~5%: restos, cata, derrames)', 'Coste ajustado por copa', '3,24 euros/copa'],
                  ].map(([concepto, detalle, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{concepto}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{detalle}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-red-50">
                    <td colSpan={2} className="p-3 font-bold text-slate-800">COSTE POR COPA (con merma)</td>
                    <td className="p-3 text-right font-bold text-red-600 text-lg">3,24 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste por copa</p>
                <p className="text-2xl font-bold text-red-600">3,24 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">8,50 euros</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-red-600">38,1%</p>
                <p className="text-xs text-red-500 font-medium">ALERTA — Revisar</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-amber-800 font-semibold mb-1">Atencion: servicio generoso</p>
              <p className="text-slate-700 text-sm">
                Con 5 copas por botella (servicio generoso de 15 cl): el coste por copa sube a <strong>3,89 euros</strong> y el food cost se dispara al <strong>45,7%</strong>. La diferencia entre 5 y 6 copas por botella es la diferencia entre perder y ganar dinero con el vino por copa.
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-8">
              <p className="text-purple-800 font-semibold mb-1">Regla del sector</p>
              <p className="text-slate-700 text-sm">
                <strong>PVP copa = precio coste botella / 3-4</strong>. Si la botella te cuesta 18,50 euros, el PVP de la copa deberia estar entre 4,63 euros y 6,17 euros como minimo para cubrir coste. Un PVP de 8,50 euros con 6 copas por botella deberia funcionar, pero la merma real lo complica. La solucion: sistemas de conservacion (gas argon, Coravin) y rotacion rapida de referencias.
              </p>
            </div>

            {/* Ejemplo 5: Cerveza de Barril */}
            <h3 id="ejemplo-cerveza" className="text-2xl font-bold text-slate-900 mb-4 scroll-mt-20">
              <span className="flex items-center gap-2"><Beer className="w-6 h-6 text-amber-500" /> Ejemplo 5: Cerveza de Barril (Cana 33cl)</span>
            </h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              La cerveza de barril es el <strong>producto de mayor rotacion</strong> en la mayoria de bares espanoles. Su escandallo incluye una merma significativa por espuma, purga de lineas y sobre-servido:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-700 text-white">
                    <th className="p-3 text-left font-medium">Concepto</th>
                    <th className="p-3 text-right font-medium">Detalle</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Barril 30 L', 'Precio distribuidor', '65,00 euros'],
                    ['Canas teoricas por barril', '30.000 ml / 330 ml = 90 canas', '0,72 euros/cana'],
                    ['Merma barril (~10%: espuma, purga, sobreservido)', 'Canas reales: ~81', '0,80 euros/cana'],
                  ].map(([concepto, detalle, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{concepto}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{detalle}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={2} className="p-3 font-bold text-slate-800">COSTE POR CANA (con merma)</td>
                    <td className="p-3 text-right font-bold text-amber-700 text-lg">0,80 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste por cana</p>
                <p className="text-2xl font-bold text-amber-700">0,80 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">2,80 euros</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-yellow-600">28,6%</p>
                <p className="text-xs text-yellow-600 font-medium">Aceptable</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Con un food cost del 28,6%, la cana de barril esta en rango aceptable. El margen por unidad es de <strong>2,00 euros</strong>, pero el volumen lo compensa: un bar que sirve 200 canas al dia genera 400 euros de margen bruto solo con cerveza de barril. La clave para mejorar el food cost: <strong>mantenimiento correcto de las lineas</strong> (reduce merma por espuma), purga minima y formacion del equipo para servir con la inclinacion correcta del vaso.
            </p>

            {/* Ejemplo 6: Zumo de Naranja Natural */}
            <h3 id="ejemplo-zumo" className="text-2xl font-bold text-slate-900 mb-4 scroll-mt-20">
              <span className="flex items-center gap-2"><GlassWater className="w-6 h-6 text-orange-500" /> Ejemplo 6: Zumo de Naranja Natural</span>
            </h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El zumo de naranja natural es un clasico de desayunos y brunchs. Su particularidad: la <strong>merma es altisima</strong> (30-40%) porque se pierde la piel, la pulpa residual y parte del jugo queda en la maquina:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-orange-700 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Naranjas de zumo', '460 g brutos', '~35% merma (piel, pulpa). Necesitas 460 g brutos para obtener ~300 ml de zumo. A 1,60 euros/kg', '0,74 euros'],
                    ['Hielo', '100 g', 'Maquina propia', '0,04 euros'],
                  ].map(([ing, qty, calc, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calc}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-orange-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-orange-600 text-lg">0,78 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-orange-600">0,78 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">3,50 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">22,3%</p>
                <p className="text-xs text-green-600 font-medium">Bien</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                El zumo natural tiene merma alta (35%). Negocia con tu proveedor el precio de <strong>naranja de zumo</strong> (mas barata que la naranja de mesa porque son piezas mas pequenas o irregulares). En temporada (noviembre-marzo), el precio puede bajar a 1,00-1,20 euros/kg, reduciendo el food cost al 15-18%. Fuera de temporada, el coste sube y conviene ofrecer alternativas. Consulta el <Link to="/calendario-temporada" className="text-purple-600 hover:underline">calendario de temporada</Link> para planificar.
              </p>
            </div>

            {/* Section 4: Como reducir el food cost */}
            <h2 id="reducir-food-cost" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Como Reducir el Food Cost de Bebidas</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Controlar el food cost de bebidas no requiere invertir grandes cantidades. Se trata de <strong>sistematizar procesos</strong> que muchos bares hacen a ojo. Estas 5 estrategias estan probadas en establecimientos reales:
            </p>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Estandarizar medidas con dosificadores y jiggers</h3>
                <p className="text-slate-700 leading-relaxed">
                  Un camarero que sirve "a ojo" puede variar entre 4 cl y 7 cl por copa. Con un dosificador de 5 cl en cada botella de licor, la medida es exacta siempre. El coste de un dosificador es de 2-3 euros y se amortiza en un dia. Para cocteles, el jigger es obligatorio: un bartender profesional nunca sirve sin medir. La diferencia entre un bar que mide y uno que no mide puede ser de <strong>500-1.000 euros al mes en merma</strong>.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Control de inventario semanal de botellas</h3>
                <p className="text-slate-700 leading-relaxed">
                  Pesa o mide el nivel de todas las botellas abiertas una vez a la semana. Compara el consumo teorico (segun tickets de venta) con el consumo real (segun inventario). La diferencia es merma real: over-pouring, invitaciones no registradas, posibles robos o errores de cobro. Un control semanal disciplinado reduce la merma invisible entre un 5% y un 15%.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Negociar con distribuidores por volumen</h3>
                <p className="text-slate-700 leading-relaxed">
                  Los distribuidores de bebidas ofrecen descuentos significativos por volumen. Comprar ginebra por cajas de 6 en vez de unidades sueltas puede ahorrarte un 10-15%. Negocia rapeles trimestrales y aprovecha las promociones de temporada. Una reduccion de 2 euros en el precio de la botella de ginebra baja el food cost del gin tonic en un 3-4%.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Disenar una carta de cocteles rentables</h3>
                <p className="text-slate-700 leading-relaxed">
                  No todos los cocteles son igual de rentables. Prioriza cocteles con pocos ingredientes premium y alto valor percibido. Un mojito (food cost 16,7%) es mas rentable que un gin tonic premium (food cost 37,2%). Diseña tu carta con un mix: 60% cocteles de alto margen + 40% premium que atraigan al publico. <Link to="/ingenieria-de-menu" className="text-purple-600 hover:underline">Aprende ingenieria de menu</Link> aplicada a bebidas.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. Happy hour estrategico con cocteles de bajo food cost</h3>
                <p className="text-slate-700 leading-relaxed">
                  Si ofreces happy hour, selecciona cocteles con food cost bajo (mojito, daikiri, margarita) y no los que ya tienen margen ajustado (gin tonic premium, whisky sour con bourbon top). Un mojito a 6 euros en happy hour (vs 9 euros normal) aun tiene un food cost del 25%: rentable. Un gin tonic premium a 8 euros en happy hour tiene un food cost del 55,8%: estas perdiendo dinero.
                </p>
              </div>
            </div>

            {/* Section 5: Mermas especificas */}
            <h2 id="mermas-bebidas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Mermas Especificas de Bebidas</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las mermas en bebidas son diferentes a las de cocina. No hay limpieza ni coccion, pero hay perdidas especificas que debes conocer y controlar. Esta tabla recoge las <strong>mermas tipicas</strong> por tipo de producto. Si tu merma real esta por encima de estos rangos, hay un problema de proceso o de equipo. Si quieres profundizar en mermas de cocina, consulta nuestra <Link to="/mermas-cocina" className="text-purple-600 hover:underline">guia completa de mermas</Link>.
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-center font-medium">Merma tipica</th>
                    <th className="p-3 text-left font-medium">Causa principal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cocteles (licores)', '3-5%', 'Over-pouring, derrames, invitaciones'],
                    ['Cerveza de barril', '8-12%', 'Espuma, purga de lineas, limpieza'],
                    ['Cerveza en botella', '0%', 'Sin merma (envase cerrado)'],
                    ['Vino por botella', '5-8%', 'Cata, restos, oxidacion de botellas abiertas'],
                    ['Zumos naturales', '30-40%', 'Piel, pulpa residual, jugo retenido en maquina'],
                    ['Licores y destilados', '2-3%', 'Evaporacion lenta, derrames, calibracion dosificador'],
                    ['Refrescos', '0%', 'Sin merma (envase cerrado)'],
                    ['Cafe', '1-2%', 'Calibracion de molienda, derrames, posos residuales'],
                  ].map(([producto, merma, causa], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{producto}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{causa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section 6: Bebidas vs Comida */}
            <h2 id="bebidas-vs-comida" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Escandallo de Bebidas vs Comida — Diferencias Clave</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aunque la logica del escandallo es la misma (ingredientes + cantidades + mermas = coste), las bebidas y la comida tienen diferencias fundamentales que afectan a como gestionas cada area:
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Aspecto</th>
                    <th className="p-3 text-center font-medium">Bebidas</th>
                    <th className="p-3 text-center font-medium">Comida</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Ingredientes por receta', '3-5', '8-20'],
                    ['Food cost objetivo', '18-22%', '28-32%'],
                    ['Margen bruto medio', '75-82%', '65-72%'],
                    ['Tipo de merma', 'Derrames, over-pouring, oxidacion', 'Limpieza, coccion, almacenamiento'],
                    ['Merma media', '2-10%', '15-35%'],
                    ['Frecuencia actualizacion precios', 'Mensual (precios estables)', 'Semanal (producto fresco)'],
                    ['Complejidad del escandallo', 'Baja', 'Media-alta'],
                    ['Mayor riesgo', 'Over-pouring, robo', 'Desperdicio, caducidad'],
                    ['Control principal', 'Dosificadores + inventario semanal', 'Pesaje + fichas tecnicas'],
                  ].map(([aspecto, bebidas, comida], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{aspecto}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-purple-700">{bebidas}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-blue-700">{comida}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              La conclusion es clara: las bebidas tienen <strong>menor complejidad pero mayor margen</strong>. Invertir una hora a la semana en controlar el escandallo de bebidas puede tener mas impacto en tu cuenta de resultados que dedicar tres horas al escandallo de comida. La combinacion ideal: controlar ambos con un <Link to="/software-escandallo" className="text-purple-600 hover:underline">software de escandallos</Link> que automatice los calculos.
            </p>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre escandallos de bebidas</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-purple-500" /> Guias relacionadas</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Escandallo en hosteleria: guia completa' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-purple-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <Wine className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Controla el coste de toda tu barra con PLEKO</h2>
            <p className="text-purple-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el escandallo de todas tus bebidas y recetas, controla mermas, escanea facturas con IA y te avisa cuando el food cost se dispara. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-purple-600 font-bold rounded-xl hover:bg-purple-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-purple-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
